import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { iemService } from '@/services/iem';
import { useRestrictedProgramLabels } from '@/hooks/useRestrictedProgramLabels';
import type {
  CatalogSizingOption,
  ExchangeReplacementOption,
  IEMAdditionalItemGroup,
  IEMBuyProgramCatalogItem,
} from '@/services/iem/types';

// IEM related query hooks (read operations)

export const usePHRItems = (personId: string, params?: Record<string, string>, enabled = true) => {
  return useQuery({
    queryKey: ['phr-items', personId, params],
    queryFn: () => iemService.getPHRItems(personId, params),
    enabled: enabled && Boolean(personId),
  });
};

export const useTHRItems = (personId: string, params?: Record<string, string>, enabled = true) => {
  return useQuery({
    queryKey: ['thr-items', personId, params],
    queryFn: () => iemService.getTHRItems(personId, params),
    enabled: enabled && Boolean(personId),
  });
};

export const useIESummary = (personId: string, params?: Record<string, string>, enabled = true) => {
  return useQuery({
    queryKey: ['ie-summary', personId, params],
    queryFn: () => iemService.getIESummary(personId, params),
    enabled: enabled && !!personId,
  });
};

export const useSizingForCustomer = (customerId: string, enabled = true) => {
  return useQuery({
    queryKey: ['sizing', customerId],
    queryFn: () => iemService.getSizingForCustomer(customerId),
    enabled: enabled && !!customerId,
  });
};

export const useCatalogChildren = ({
  parentCatalogIds,
  enabled = true,
}: {
  parentCatalogIds: string[];
  enabled?: boolean;
}) => {
  const normalizedParentIds = [...parentCatalogIds]
    .map((id) => (typeof id === 'string' ? id.trim() : ''))
    .filter((id): id is string => Boolean(id && id.length > 0))
    .sort();

  return useQuery<CatalogSizingOption[]>({
    queryKey: ['catalog-children', normalizedParentIds],
    queryFn: () => iemService.getCatalogChildren(normalizedParentIds),
    enabled: enabled && normalizedParentIds.length > 0,
    staleTime: 60_000,
  });
};

/**
 * Fetches ALL catalog children in 1-2 requests (parent_id IS NOT NULL) rather
 * than batching by specific parent IDs. Use this when you need the full
 * child catalog for a large parent set (e.g. the additional-items workflow).
 */
export const useAllCatalogChildren = ({ enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery<CatalogSizingOption[]>({
    queryKey: ['catalog-children-all'],
    queryFn: () => iemService.getAllCatalogChildren(),
    enabled,
    staleTime: 60_000,
  });
};

export const useExchangeReplacementOptions = ({
  catalogGroupIds,
  excludeAssetIds = [],
  enabled = true,
}: {
  catalogGroupIds: string[];
  excludeAssetIds?: string[];
  enabled?: boolean;
}) => {
  const sortedGroupIds = [...catalogGroupIds].sort();
  const sortedExclusions = [...excludeAssetIds].sort();

  return useQuery<ExchangeReplacementOption[]>({
    queryKey: [
      'exchange-replacement-options',
      { catalogGroupIds: sortedGroupIds, excludeAssetIds: sortedExclusions },
    ],
    queryFn: () =>
      iemService.getExchangeReplacementOptions({
        catalogGroupIds: sortedGroupIds,
        excludeAssetIds: sortedExclusions,
      }),
    enabled: enabled && sortedGroupIds.length > 0,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
};

/**
 * Loads additional-item groups with progressive background pagination.
 *
 * - First page (up to 1000 rows) resolves immediately → isLoading=false.
 * - Remaining pages stream in the background → isLoadingMore=true until done.
 * - `data.length` (and therefore the pagination count) grows as pages arrive.
 */
export const useIEMAdditionalItemGroups = ({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) => {
  const PAGE_SIZE = 1000;
  const RATE_LIMIT_RETRY_DELAY_MS = 60_000;
  const [data, setData] = useState<IEMAdditionalItemGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const cancelledRef = useRef(false);
  const enabledRef = useRef(enabled);
  const hasLoadedRef = useRef(false);
  const rateLimitRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isRateLimitError = useCallback((error: unknown): boolean => {
    const status =
      typeof error === 'object' && error !== null && 'status' in error
        ? (error as { status?: unknown }).status
        : undefined;
    return status === 429;
  }, []);

  const clearRateLimitRetry = useCallback(() => {
    if (rateLimitRetryTimeoutRef.current) {
      clearTimeout(rateLimitRetryTimeoutRef.current);
      rateLimitRetryTimeoutRef.current = null;
    }
  }, []);

  const load = useCallback(async () => {
    cancelledRef.current = false;
    setIsLoading(true);
    setIsLoadingMore(false);
    try {
      const firstPage = await iemService.getAdditionalItemGroups({
        limit: PAGE_SIZE,
        offset: 0,
      });
      if (cancelledRef.current) return;
      clearRateLimitRetry();
      hasLoadedRef.current = true;
      setData(firstPage);
      setIsLoading(false);

      if (firstPage.length < PAGE_SIZE) return;

      // Background: load remaining pages one at a time
      setIsLoadingMore(true);
      let offset = PAGE_SIZE;
      let accumulated = firstPage;
      while (!cancelledRef.current) {
        const page = await iemService.getAdditionalItemGroups({
          limit: PAGE_SIZE,
          offset,
        });
        if (cancelledRef.current) break;
        accumulated = [...accumulated, ...page];
        setData(accumulated);
        if (page.length < PAGE_SIZE) break;
        offset += PAGE_SIZE;
      }
    } catch (error) {
      if (!cancelledRef.current && isRateLimitError(error) && !rateLimitRetryTimeoutRef.current) {
        rateLimitRetryTimeoutRef.current = setTimeout(() => {
          rateLimitRetryTimeoutRef.current = null;
          if (!cancelledRef.current && enabledRef.current) {
            void load();
          }
        }, RATE_LIMIT_RETRY_DELAY_MS);
      }
    } finally {
      if (!cancelledRef.current) {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  }, [clearRateLimitRetry, isRateLimitError]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      cancelledRef.current = true;
      setIsLoading(false);
      setIsLoadingMore(false);
      clearRateLimitRetry();
      return;
    }

    cancelledRef.current = false;
    if (hasLoadedRef.current) {
      return;
    }

    void load();

    return () => {
      cancelledRef.current = true;
      clearRateLimitRetry();
    };
  }, [clearRateLimitRetry, enabled, load]);

  const refetch = useCallback(() => {
    hasLoadedRef.current = false;
    clearRateLimitRetry();
    void load();
  }, [clearRateLimitRetry, load]);

  return { data, isLoading, isLoadingMore, refetch };
};

export const useIEMBuyProgramCatalog = ({
  searchTerm,
  limit,
  enabled = true,
}: {
  searchTerm?: string;
  limit?: number;
  enabled?: boolean;
} = {}) => {
  return useQuery<IEMBuyProgramCatalogItem[]>({
    queryKey: ['iem-buy-program-catalog', { searchTerm, limit }],
    queryFn: () => iemService.getBuyProgramCatalog({ searchTerm, limit }),
    enabled,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
};

export const useIEMRestrictedProgramCatalog = ({
  searchTerm,
  limit,
  enabled = true,
}: {
  searchTerm?: string;
  limit?: number;
  enabled?: boolean;
} = {}) => {
  const { programLabel } = useRestrictedProgramLabels();
  return useQuery<IEMBuyProgramCatalogItem[]>({
    queryKey: ['iem-restricted-program-catalog', { searchTerm, limit }],
    queryFn: () => iemService.getRestrictedProgramCatalog({ searchTerm, limit, programLabel }),
    enabled,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
};
